import * as React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { UpdateProjectSchema, type UpdateProjectInput, type ProjectDto } from '@cutlyft/zod-schemas';
import { useUpdateProject } from '@/hooks/projects/use-update-project';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

interface EditProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: ProjectDto;
}

type EditFormValues = {
  name: string;
  reference: string;
  clientName: string;
  clientContact: string;
  defaultKerfMm: string;
  defaultStockBarMm: string;
  defaultOffcutThresholdMm: string;
  remarks: string;
};

export function EditProjectModal({ open, onOpenChange, project }: EditProjectModalProps) {
  const { toast } = useToast();
  const updateMutation = useUpdateProject();

  const form = useForm<EditFormValues>({
    defaultValues: {
      name: project.name,
      reference: project.reference ?? '',
      clientName: project.clientName ?? '',
      clientContact: project.clientContact ?? '',
      defaultKerfMm: project.defaultKerfMm != null ? String(project.defaultKerfMm) : '',
      defaultStockBarMm: project.defaultStockBarMm != null ? String(project.defaultStockBarMm) : '',
      defaultOffcutThresholdMm:
        project.defaultOffcutThresholdMm != null ? String(project.defaultOffcutThresholdMm) : '',
      remarks: project.remarks ?? '',
    },
  });

  React.useEffect(() => {
    if (open) {
      form.reset({
        name: project.name,
        reference: project.reference ?? '',
        clientName: project.clientName ?? '',
        clientContact: project.clientContact ?? '',
        defaultKerfMm: project.defaultKerfMm != null ? String(project.defaultKerfMm) : '',
        defaultStockBarMm: project.defaultStockBarMm != null ? String(project.defaultStockBarMm) : '',
        defaultOffcutThresholdMm:
          project.defaultOffcutThresholdMm != null ? String(project.defaultOffcutThresholdMm) : '',
        remarks: project.remarks ?? '',
      });
    }
  }, [open, project, form]);

  const onSubmit = (values: EditFormValues) => {
    const kerfNum = parseInt(values.defaultKerfMm, 10);
    const stockNum = parseInt(values.defaultStockBarMm, 10);
    const offcutNum = parseInt(values.defaultOffcutThresholdMm, 10);

    const payload: UpdateProjectInput = {
      ...(values.name.trim() && { name: values.name.trim() }),
      ...(values.reference.trim() && { reference: values.reference.trim() }),
      ...(values.clientName.trim() && { clientName: values.clientName.trim() }),
      ...(values.clientContact.trim() && { clientContact: values.clientContact.trim() }),
      ...(!isNaN(kerfNum) && kerfNum > 0 && { defaultKerfMm: kerfNum }),
      ...(!isNaN(stockNum) && stockNum > 0 && { defaultStockBarMm: stockNum }),
      ...(!isNaN(offcutNum) && offcutNum > 0 && { defaultOffcutThresholdMm: offcutNum }),
      ...(values.remarks.trim() && { remarks: values.remarks.trim() }),
    };

    const parsed = UpdateProjectSchema.safeParse(payload);
    if (!parsed.success) {
      toast({ variant: 'destructive', title: 'Validation error', description: parsed.error.issues[0]?.message });
      return;
    }

    updateMutation.mutate(
      { id: project.id, input: parsed.data },
      {
        onSuccess: () => {
          toast({ title: 'Project updated' });
          onOpenChange(false);
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? '';
          toast({ variant: 'destructive', title: 'Failed to update project', description: msg || 'An unexpected error occurred.' });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogDescription>Update project details and default cutting parameters.</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference / Job Number</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. JOB-2026-001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="clientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clientContact"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Client Contact</FormLabel>
                    <FormControl>
                      <Input placeholder="Email / phone" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <FormField
                control={form.control}
                name="defaultKerfMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kerf (mm)</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={20} placeholder="e.g. 4" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultStockBarMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stock Bar (mm)</FormLabel>
                    <FormControl>
                      <Input type="number" min={1000} max={12000} placeholder="e.g. 6000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="defaultOffcutThresholdMm"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Offcut Min (mm)</FormLabel>
                    <FormControl>
                      <Input type="number" min={50} max={2000} placeholder="e.g. 300" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="remarks"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Remarks</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional notes" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
